/**
 * Modal shown when the user clicks the status glyph on a vehicle row.
 *
 * Resolves the VAUTO match — Rapid Recon notes FOLLOW the VAUTO pick, so this
 * modal only deals with VAUTO:
 *  - VAUTO section: candidate picker (ambiguous / change-pick), a resolved
 *    note, or the no-match alert. Each candidate shows whether recon notes
 *    will come with it.
 *  - Other issues: any remaining alerts (e.g. unmapped rooftop).
 */
import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

import {
  AuctionAlert,
  AuctionMatchCandidate,
  AuctionVehicle,
} from "../../api/wholesaleAuction"
import { THEME } from "../../config/theme"
import { useWholesaleAuction } from "../../context/WholesaleAuctionContext"
import { formatMoney } from "../../utils/wholesaleAuctionFormat"

// Alert codes already surfaced by the VAUTO section — anything else
// (e.g. unmapped_rooftop) falls through to the "Other issues" section.
const MATCH_ALERT_CODES = new Set(["ambiguous_match", "no_vauto_match"])

interface Props {
  vehicle: AuctionVehicle
  onClose: () => void
}

export default function AuctionRowAlertModal({ vehicle, onClose }: Props) {
  // Esc-to-close — UX expectation for any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  const subtitle = buildSubtitle(vehicle)
  const otherAlerts = vehicle.alerts.filter((a) => !MATCH_ALERT_CODES.has(a.code))

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.md,
          padding: THEME.spacing.lg,
          minWidth: 0,
          width: "min(640px, 94vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Header title="Resolve Row" subtitle={subtitle} onClose={onClose} />
        <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.lg }}>
          <VautoSection vehicle={vehicle} onClose={onClose} />
          {otherAlerts.length > 0 && (
            <div>
              <SectionHeading>Other Issues</SectionHeading>
              <Body alerts={otherAlerts} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: THEME.typography.caption.size,
        fontWeight: THEME.typography.caption.weight,
        letterSpacing: THEME.typography.caption.tracking,
        textTransform: "uppercase",
        color: THEME.colors.neutral.light.textSecondary,
        marginBottom: THEME.spacing.sm,
      }}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// VAUTO section
// ---------------------------------------------------------------------------

function VautoSection({ vehicle, onClose }: { vehicle: AuctionVehicle; onClose: () => void }) {
  const candidates = vehicle.match_candidates ?? []
  const resolved = vehicle.match_state === "user_selected"
  const noMatch = vehicle.match_state === "no_match"

  // A clean matched row with nothing to pick — omit the section entirely.
  if (candidates.length === 0 && !resolved && !noMatch) return null

  return (
    <div>
      <SectionHeading>VAUTO Match</SectionHeading>
      {candidates.length > 0 ? (
        <CandidatePicker
          vehicle={vehicle}
          candidates={candidates}
          selectedStock={resolved ? vehicle.vauto_stock : null}
          onClose={onClose}
        />
      ) : resolved ? (
        <ResolvedNote text="This row's VAUTO data was set manually — there are no alternate matches to choose from. Click the row to edit its data." />
      ) : (
        <Body alerts={vehicle.alerts.filter((a) => a.code === "no_vauto_match")} />
      )}
    </div>
  )
}

interface HeaderProps {
  title: string
  subtitle: string
  onClose: () => void
}

function Header({ title, subtitle, onClose }: HeaderProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: THEME.spacing.md,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: THEME.colors.neutral.light.textSecondary,
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "none",
          border: "none",
          color: THEME.colors.neutral.light.textSecondary,
          cursor: "pointer",
          padding: 4,
        }}
        aria-label="Close"
      >
        <X size={18} strokeWidth={2} />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Candidate picker (ambiguous-match resolution)
// ---------------------------------------------------------------------------

interface CandidatePickerProps {
  // The Adesa row being resolved — its client_id (stock key) and VIN are the
  // baseline each candidate's Stock/VIN are toned against.
  vehicle: AuctionVehicle
  candidates: AuctionMatchCandidate[]
  // The vauto_stock currently applied to the row (when already resolved), so
  // the picker can mark it and let the user switch. null when not yet resolved.
  selectedStock: string | null
  onClose: () => void
}

function CandidatePicker({ vehicle, candidates, selectedStock, onClose }: CandidatePickerProps) {
  const { selectCandidate } = useWholesaleAuction()
  const [savingStock, setSavingStock] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onChoose = async (candidate: AuctionMatchCandidate) => {
    if (!candidate.vauto_stock || candidate.vauto_stock === selectedStock) return
    setSavingStock(candidate.vauto_stock)
    setError(null)
    try {
      await selectCandidate(vehicle.id, candidate.vauto_stock)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Selection failed")
      setSavingStock(null)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.md }}>
      <div
        style={{
          fontSize: 12,
          color: THEME.colors.neutral.light.textSecondary,
          lineHeight: 1.4,
        }}
      >
        {selectedStock
          ? "You can change which VAUTO record fills in this vehicle — pick a different one if the wrong match was chosen."
          : "VIN and Stock # didn't agree on a single VAUTO record. Pick the one to use — its data will fill in this vehicle and mark the row resolved."}
      </div>
      <AdesaReference vehicle={vehicle} />
      {candidates.map((candidate) => (
        <CandidateCard
          key={candidate.vauto_stock ?? candidate.matched_on}
          vehicle={vehicle}
          candidate={candidate}
          isSelected={!!selectedStock && candidate.vauto_stock === selectedStock}
          saving={savingStock === candidate.vauto_stock}
          disabled={savingStock != null}
          onChoose={() => onChoose(candidate)}
        />
      ))}
      {error && (
        <div style={{ fontSize: 12, color: THEME.colors.semantic.danger }}>{error}</div>
      )}
    </div>
  )
}

function CandidateCard({
  vehicle,
  candidate,
  isSelected,
  saving,
  disabled,
  onChoose,
}: {
  vehicle: AuctionVehicle
  candidate: AuctionMatchCandidate
  isSelected: boolean
  saving: boolean
  disabled: boolean
  onChoose: () => void
}) {
  const vehicleLabel = [candidate.year_vauto, candidate.make_vauto, candidate.model_vauto]
    .filter(Boolean)
    .join(" ")
  const matchedLabel = candidate.matched_on === "vin" ? "VIN match" : "Stock match"

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: THEME.spacing.xs,
        padding: THEME.spacing.sm,
        background: isSelected ? "rgba(37, 99, 235, 0.06)" : THEME.colors.neutral.light.surface,
        border: `1px solid ${isSelected ? THEME.colors.semantic.info : THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.sm,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: THEME.colors.semantic.info,
            background: "rgba(37, 99, 235, 0.1)",
            padding: "2px 6px",
            borderRadius: THEME.borderRadius.sm,
          }}
        >
          {matchedLabel}
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          {vehicleLabel || "Vehicle"}
        </span>
        {isSelected && (
          <span style={{ fontSize: 12, fontWeight: 600, color: THEME.colors.semantic.info }}>
            ✓ Currently using
          </span>
        )}
      </div>
      <CandidateIdentifiers vehicle={vehicle} candidate={candidate} />
      {candidate.vauto_store && (
        <div style={{ fontSize: 12, color: THEME.colors.neutral.light.textSecondary }}>
          {candidate.vauto_store}
        </div>
      )}
      <div style={{ fontSize: 12, color: THEME.colors.neutral.light.textSecondary }}>
        Unit {formatMoney(candidate.unit_cost)} · KBB Lend {formatMoney(candidate.kbb_lending)} · KBB Trade {formatMoney(candidate.kbb_trade_in)}
      </div>
      {/* Recon follows the pick: tell the user notes will attach with this candidate.
          Driven by rr_matches (the per-source note list), so an empty duplicate in
          another recon file no longer masks a real note. */}
      <div style={{ fontSize: 12, color: THEME.colors.neutral.light.textSecondary, fontStyle: "italic" }}>
        {candidate.rr_matches && candidate.rr_matches.length > 0
          ? "✓ Rapid Recon notes will attach with this match"
          : "No Rapid Recon notes for this stock"}
      </div>
      {!isSelected && (
        <button
          type="button"
          onClick={onChoose}
          disabled={disabled}
          style={{
            alignSelf: "flex-start",
            marginTop: THEME.spacing.xs,
            padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
            fontSize: 13,
            fontWeight: 500,
            fontFamily: THEME.fonts.primary,
            color: THEME.colors.white,
            background: THEME.colors.navy,
            border: "none",
            borderRadius: THEME.borderRadius.md,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled && !saving ? 0.5 : 1,
          }}
        >
          {saving ? "Applying…" : "Use this match"}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Adesa reference + per-candidate identifiers. The Adesa vehicle being resolved
// is shown ONCE as plain text above the candidates; each VAUTO candidate is
// structured the same way (year/make/model in its card title → Stock → VIN) but
// with match tones against that Adesa reference: green = matches Adesa, yellow =
// differs, red = the candidate has no value. Stock is compared against CLIENT
// ID # (the dealer stock that lines up with VAUTO), not the auction stock.
// ---------------------------------------------------------------------------

type FieldTone = "match" | "mismatch" | "missing" | "none"

const norm = (s: string | null | undefined): string => (s ?? "").trim().toUpperCase()

// Tone for a candidate identifier against the Adesa value. Red when the
// candidate itself has no value; plain ("none") when there's no Adesa value to
// compare against; otherwise green/yellow on agreement.
function candidateTone(adesaValue: string | null, candidateValue: string | null): FieldTone {
  const c = norm(candidateValue)
  if (c === "") return "missing"
  const a = norm(adesaValue)
  if (a === "") return "none"
  return a === c ? "match" : "mismatch"
}

function ymm(year: number | null, make: string | null, model: string | null): string {
  return [year, make, model].filter(Boolean).join(" ")
}

// The Adesa vehicle being resolved — plain text, shown once above the
// candidates so each VAUTO option can be compared back to it.
function AdesaReference({ vehicle }: { vehicle: AuctionVehicle }) {
  const label = ymm(vehicle.year_adesa, vehicle.make_adesa, vehicle.model_adesa)
  return (
    <div style={referenceStyle}>
      <div style={referenceTagStyle}>Adesa Run List</div>
      {label && <div style={referenceTitleStyle}>{label}</div>}
      <div style={referenceLineStyle}>Stock {vehicle.client_id?.trim() || "—"}</div>
      <div style={referenceLineStyle}>VIN {vehicle.vin || "—"}</div>
    </div>
  )
}

// One VAUTO candidate's Stock + VIN, toned against the Adesa reference above.
function CandidateIdentifiers({
  vehicle,
  candidate,
}: {
  vehicle: AuctionVehicle
  candidate: AuctionMatchCandidate
}) {
  const stockTone = candidateTone(vehicle.client_id, candidate.vauto_stock)
  const vinTone = candidateTone(vehicle.vin, candidate.vauto_vin)
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <IdField label="Stock" value={candidate.vauto_stock} tone={stockTone} />
      <IdField label="VIN" value={candidate.vauto_vin} tone={vinTone} />
    </div>
  )
}

function IdField({ label, value, tone }: { label: string; value: string | null; tone: FieldTone }) {
  return (
    <div style={idFieldRowStyle}>
      <span style={idLabelStyle}>{label}</span>
      {tone === "missing" ? (
        <span style={missingChipStyle}>Missing</span>
      ) : (
        <>
          <span style={valueChipStyle(tone)}>{value || "—"}</span>
          <Marker tone={tone} />
        </>
      )}
    </div>
  )
}

function Marker({ tone }: { tone: FieldTone }) {
  if (tone === "match") return <span style={matchedMarkerStyle}>∗ Matched</span>
  if (tone === "mismatch") return <span style={mismatchMarkerStyle}>∗ Mismatched</span>
  return null
}

const referenceStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const referenceTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: 2,
}

const referenceTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const referenceLineStyle: React.CSSProperties = {
  fontSize: 12,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  color: THEME.colors.neutral.light.textSecondary,
}

const idFieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  flexWrap: "wrap",
}

const idLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
  minWidth: 34,
}

// Plain when there's nothing to compare ("none"); green/yellow tint otherwise.
function valueChipStyle(tone: FieldTone): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    color: THEME.colors.neutral.light.textPrimary,
    borderRadius: THEME.borderRadius.sm,
  }
  if (tone === "none") return base
  return {
    ...base,
    padding: "1px 6px",
    background: tone === "match" ? THEME.colors.success : THEME.colors.warning,
  }
}

const missingChipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: 12,
  fontWeight: 600,
  color: THEME.colors.semantic.danger,
  background: THEME.colors.danger,
  borderRadius: THEME.borderRadius.sm,
  padding: "1px 6px",
}

const matchedMarkerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: THEME.colors.semantic.success,
  fontFamily: THEME.fonts.primary,
  marginLeft: 6,
}

const mismatchMarkerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: THEME.colors.semantic.warning,
  fontFamily: THEME.fonts.primary,
  marginLeft: 6,
}

function ResolvedNote({ text }: { text: string }) {
  return (
    <div
      style={{
        display: "flex",
        gap: THEME.spacing.sm,
        padding: THEME.spacing.sm,
        background: "rgba(37, 99, 235, 0.06)",
        border: `1px solid ${THEME.colors.semantic.info}`,
        borderRadius: THEME.borderRadius.sm,
      }}
    >
      <span
        aria-hidden
        style={{
          color: THEME.colors.semantic.info,
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1,
          width: 18,
          flexShrink: 0,
        }}
      >
        ✓
      </span>
      <div style={{ fontSize: 12, color: THEME.colors.neutral.light.textSecondary, lineHeight: 1.4 }}>
        {text}
      </div>
    </div>
  )
}

function Body({ alerts }: { alerts: AuctionAlert[] }) {
  if (alerts.length === 0) {
    return (
      <div
        style={{
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          fontStyle: "italic",
        }}
      >
        No issues.
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.md }}>
      {alerts.map((alert) => (
        <AlertRow key={alert.code} alert={alert} />
      ))}
    </div>
  )
}

function AlertRow({ alert }: { alert: AuctionAlert }) {
  return (
    <div
      style={{
        display: "flex",
        gap: THEME.spacing.sm,
        padding: THEME.spacing.sm,
        background: "rgba(220, 38, 38, 0.05)",
        border: `0.5px solid rgba(220, 38, 38, 0.2)`,
        borderRadius: THEME.borderRadius.sm,
      }}
    >
      <span
        aria-hidden
        style={{
          color: THEME.colors.semantic.danger,
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1,
          width: 18,
          flexShrink: 0,
        }}
      >
        ✗
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: THEME.colors.neutral.light.textPrimary,
            marginBottom: 2,
          }}
        >
          {alert.title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: THEME.colors.neutral.light.textSecondary,
            lineHeight: 1.4,
          }}
        >
          {alert.detail}
        </div>
      </div>
    </div>
  )
}

function buildSubtitle(vehicle: AuctionVehicle): string {
  // VIN is intentionally omitted — the VIN is what the user is resolving, and
  // it's already shown in the Adesa reference block below. Run before Lane.
  const parts: string[] = []
  if (vehicle.run_number != null) parts.push(`Run #${vehicle.run_number}`)
  if (vehicle.lane) parts.push(`Lane ${vehicle.lane}`)
  return parts.join(", ") || "Vehicle"
}
